import { get } from "lodash";
import {
  AUTHORITY_TYPE,
  NIR_INHERITANCE_FROM,
  REGEX_EXPS,
} from "utils/constants";
import {
  childUsesStrictNirValidation,
  childHasSocialRegimeAttachmentMismatch,
  getAgeFromBirthDate,
  isChildLinkedToSpouse,
} from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/relatedPersonChild";
import { normalizeTextField } from "utils/helpers/validations";
import { softValidateNIR, validateNIR } from "utils/helpers/values";
import * as yup from "yup";

const validation = (
  messages,
  { hasPartner = false, adherentSocialRegime = "" } = {},
) => {
  const string = yup
    .string()
    .required(messages.REQUIRED)
    .typeError(messages.NOT_STRING)
    .nullable();

  const nameValidation = yup
    .string()
    .transform(normalizeTextField)
    .required(messages.REQUIRED)
    .test("is-valid-name", messages.INVALID_NAME, (value) => {
      return !value || REGEX_EXPS.name.test(value);
    });

  const spouseValidationSchema = yup.object().shape({
    identity: yup.object().shape({
      user: yup.object().shape({
        lastname: nameValidation.required(messages.REQUIRED),
        firstname: nameValidation.required(messages.REQUIRED),
        gender: string,
      }),
      socialSecurityNumber: yup
        .string()
        .typeError(messages.REQUIRED)
        .required(messages.REQUIRED)
        .test(
          "validateNIR",
          messages.INVALID_SOCIAL_SECURITY_NUMBER,
          function (value, ctx) {
            const result = validateNIR(value, this.parent);
            if (!result.valid)
              return ctx.createError({
                message: messages[result.reason] || messages.INVALID_VALUE,
              });

            return true;
          },
        ),
      socialRegime: yup
        .string()
        .typeError(messages.REQUIRED)
        .required(messages.REQUIRED)
        .test(
          "spouse-regime-children",
          messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
          function (value) {
            const root = this.from[this.from.length - 1].value;
            const children = get(root, "children", []);
            const hasMismatch = children.some(
              (child) =>
                isChildLinkedToSpouse({
                  authority: get(child, "authority"),
                  nirInheritanceFrom: get(
                    child,
                    "identity.fields.nirInheritanceFrom",
                  ),
                  hasSpouse: hasPartner,
                }) &&
                childHasSocialRegimeAttachmentMismatch({
                  child,
                  adherentSocialRegime,
                  spouseSocialRegime: value,
                  hasSpouse: hasPartner,
                }),
            );
            if (!hasMismatch) return true;
            return this.createError({
              message: messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
            });
          },
        ),
    }),
  });

  const childrenValidationSchema = yup
    .object()
    .shape({
      authority: yup
        .string()
        .nullable()
        .test("required-if-minor", messages.REQUIRED, function (value) {
          const birthdate = this.parent?.identity?.user?.birthDate;
          const age = getAgeFromBirthDate(birthdate);
          return age >= 18 || (!!value && value.length > 0);
        })
        .test("both-spouse", messages.INVALID_VALUE, function (value) {
          if (value !== AUTHORITY_TYPE.BOTH) return true;
          if (
            get(this.parent, "identity.fields.nirInheritanceFrom") !==
            NIR_INHERITANCE_FROM.SPOUSE
          ) {
            return true;
          }
          // Valid with or without spouse on contract (off-contract NIR on child row).
          return true;
        })
        .test(
          "authority-regime-match",
          messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
          function (value) {
            const child = this.parent;
            const root = this.from[this.from.length - 1].value;
            if (
              !childHasSocialRegimeAttachmentMismatch({
                child: { ...child, authority: value },
                adherentSocialRegime,
                spouseSocialRegime: get(root, "spouse.identity.socialRegime"),
                hasSpouse: hasPartner,
              })
            ) {
              return true;
            }
            return this.createError({
              message: messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
            });
          },
        ),
      identity: yup.object().shape({
        user: yup.object().shape({
          lastname: nameValidation.required(messages.REQUIRED),
          firstname: nameValidation.required(messages.REQUIRED),
          gender: string,
          birthDate: yup.date().required(messages.REQUIRED),
        }),
        socialRegime: yup
          .string()
          .typeError(messages.REQUIRED)
          .required(messages.REQUIRED),
        socialSecurityNumber: yup
          .string()
          .typeError(messages.REQUIRED)
          .required(messages.REQUIRED),
        fields: yup.object().nullable().notRequired(),
      }),
    })
    .test("both-nir-source-fields", messages.REQUIRED, function (child) {
      if (child?.authority !== AUTHORITY_TYPE.BOTH) return true;
      const v = get(child, "identity.fields.nirInheritanceFrom");
      if (
        v === NIR_INHERITANCE_FROM.ADHERENT ||
        v === NIR_INHERITANCE_FROM.SPOUSE
      ) {
        return true;
      }
      return this.createError({
        path: `${this.path}.identity.fields.nirInheritanceFrom`,
        message: messages.REQUIRED,
      });
    })
    .test(
      "child-social-regime-attachment",
      messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
      function (child) {
        if (
          !childHasSocialRegimeAttachmentMismatch({
            child,
            adherentSocialRegime,
            spouseSocialRegime: get(
              this.from[this.from.length - 1].value,
              "spouse.identity.socialRegime",
            ),
            hasSpouse: hasPartner,
          })
        ) {
          return true;
        }
        return this.createError({
          path: `${this.path}.identity.socialRegime`,
          message: messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
        });
      },
    )
    .test(
      "child-nir-format",
      messages.INVALID_SOCIAL_SECURITY_NUMBER,
      function (child) {
        const value = get(child, "identity.socialSecurityNumber");
        if (!value) return true;
        const authority = child?.authority;
        const result = childUsesStrictNirValidation(authority)
          ? validateNIR(value, child.identity)
          : softValidateNIR(value);
        if (!result.valid) {
          return this.createError({
            path: `${this.path}.identity.socialSecurityNumber`,
            message: messages[result.reason] || messages.INVALID_VALUE,
          });
        }
        return true;
      },
    );

  return yup.object().shape({
    spouse: hasPartner ? spouseValidationSchema : yup.mixed().notRequired(),
    children: yup.array().of(childrenValidationSchema),
  });
};

export default validation;
